import React, { useState } from 'react';
import { CharacterProfile } from '../types/myWealth';
import './CharacterCustomizerModal.css';

interface CharacterCustomizerModalProps {
  initialProfile: CharacterProfile;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedProfile: CharacterProfile) => Promise<void>;
}

const SKIN_TONES = ['#f5cba7', '#fcd34d', '#e0ac69', '#c68642', '#8d5524', '#3d2314'];
const HAIR_COLORS = ['#4a2e18', '#1c1917', '#eab308', '#ea580c', '#9333ea', '#0284c7'];
const CLOTHING_COLORS = [
  '#3498db',
  '#e74c3c',
  '#2ecc71',
  '#9b59b6',
  '#f39c12',
  '#1abc9c',
  '#34495e',
  '#ffffff'
];
const PANTS_COLORS = ['#2c3e50', '#1e293b', '#3b82f6', '#0f172a', '#78350f', '#475569'];
const HOUSE_THEMES: Array<'classic' | 'modern' | 'cozy' | 'botanical' | 'synthwave'> = [
  'classic',
  'modern',
  'cozy',
  'botanical',
  'synthwave'
];

export const CharacterCustomizerModal: React.FC<CharacterCustomizerModalProps> = ({
  initialProfile,
  isOpen,
  onClose,
  onSave
}) => {
  const [profile, setProfile] = useState<CharacterProfile>(initialProfile);
  const [activeTab, setActiveTab] = useState<'character' | 'house' | 'profile'>('character');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg('');
    try {
      await onSave(profile);
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to save character profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="customizer-modal-overlay" role="dialog" aria-modal="true" aria-label="Customize Character and Public House">
      <div className="customizer-modal-card">
        <div className="customizer-header">
          <div>
            <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '1.25rem' }}>
              🎨 Customize Character & Public World
            </h3>
            <p style={{ margin: '4px 0 0', color: '#94a3b8', fontSize: '0.82rem' }}>
              Changes made here are safe for public display and never reveal financial data.
            </p>
          </div>
          <button type="button" className="close-x-btn" onClick={onClose} aria-label="Close customizer">
            ✕
          </button>
        </div>

        {errorMsg && <div className="modal-error-banner">{errorMsg}</div>}

        <div className="customizer-nav-tabs">
          <button
            type="button"
            className={`modal-tab-btn ${activeTab === 'character' ? 'active' : ''}`}
            onClick={() => setActiveTab('character')}
          >
            👤 Avatar Appearance
          </button>
          <button
            type="button"
            className={`modal-tab-btn ${activeTab === 'house' ? 'active' : ''}`}
            onClick={() => setActiveTab('house')}
          >
            🏡 Public House Theme
          </button>
          <button
            type="button"
            className={`modal-tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            🌐 Public Profile Settings
          </button>
        </div>

        <form onSubmit={handleSave} className="customizer-form-body">
          {/* TAB 1: CHARACTER CUSTOMIZATION */}
          {activeTab === 'character' && (
            <div className="customizer-fields-list">
              {/* Skin Tone */}
              <div className="swatch-field-group">
                <label>Skin Tone</label>
                <div className="swatches-row">
                  {SKIN_TONES.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`swatch-circle ${profile.skinTone === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setProfile({ ...profile, skinTone: color })}
                      aria-label={`Skin tone ${color}`}
                    />
                  ))}
                </div>
              </div>

              {/* Hair Color */}
              <div className="swatch-field-group">
                <label>Hair Color</label>
                <div className="swatches-row">
                  {HAIR_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`swatch-circle ${profile.hairColor === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setProfile({ ...profile, hairColor: color })}
                      aria-label={`Hair color ${color}`}
                    />
                  ))}
                </div>
              </div>

              {/* Shirt Color */}
              <div className="swatch-field-group">
                <label>Shirt Color</label>
                <div className="swatches-row">
                  {CLOTHING_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`swatch-circle ${profile.shirtColor === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setProfile({ ...profile, shirtColor: color })}
                      aria-label={`Shirt color ${color}`}
                    />
                  ))}
                </div>
              </div>

              {/* Pants Color */}
              <div className="swatch-field-group">
                <label>Pants Color</label>
                <div className="swatches-row">
                  {PANTS_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`swatch-circle ${profile.pantsColor === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setProfile({ ...profile, pantsColor: color })}
                      aria-label={`Pants color ${color}`}
                    />
                  ))}
                </div>
              </div>

              {/* Accessory */}
              <div className="input-group">
                <label htmlFor="accessory-select">Accessory</label>
                <select
                  id="accessory-select"
                  className="modal-select"
                  value={profile.accessory}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      accessory: e.target.value as any
                    })
                  }
                >
                  <option value="none">None</option>
                  <option value="glasses">Glasses 👓</option>
                  <option value="cap">Sport Cap 🧢</option>
                  <option value="watch">Smartwatch ⌚</option>
                </select>
              </div>
            </div>
          )}

          {/* TAB 2: PUBLIC HOUSE THEME & DECORATIONS */}
          {activeTab === 'house' && (
            <div className="customizer-fields-list">
              <div className="input-group">
                <label htmlFor="house-theme-select">Public House Theme</label>
                <select
                  id="house-theme-select"
                  className="modal-select"
                  value={profile.publicHouseTheme}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      publicHouseTheme: e.target.value as any
                    })
                  }
                >
                  {HOUSE_THEMES.map((theme) => (
                    <option key={theme} value={theme}>
                      {theme.charAt(0).toUpperCase() + theme.slice(1)} Room Style
                    </option>
                  ))}
                </select>
              </div>

              <div className="input-group">
                <label htmlFor="plant-count-range">
                  Terrace Houseplants: {profile.publicDecorations?.plantCount ?? 2}
                </label>
                <input
                  id="plant-count-range"
                  type="range"
                  min="0"
                  max="5"
                  value={profile.publicDecorations?.plantCount ?? 2}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      publicDecorations: {
                        ...profile.publicDecorations,
                        plantCount: parseInt(e.target.value, 10)
                      }
                    })
                  }
                />
              </div>

              <div className="checkbox-field-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={profile.publicDecorations?.hasAquarium ?? false}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        publicDecorations: {
                          ...profile.publicDecorations,
                          hasAquarium: e.target.checked
                        }
                      })
                    }
                  />
                  <span>Feature Living Room Aquarium 🐠</span>
                </label>
              </div>
            </div>
          )}

          {/* TAB 3: PUBLIC PROFILE SETTINGS */}
          {activeTab === 'profile' && (
            <div className="customizer-fields-list">
              <div className="input-group">
                <label htmlFor="modal-username">Public Username / Slug</label>
                <input
                  id="modal-username"
                  type="text"
                  className="modal-input"
                  value={profile.username}
                  onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                  placeholder="e.g. harivishnu"
                  required
                />
                <span className="field-hint">Used for your public URL: /profile/{profile.username}</span>
              </div>

              <div className="input-group">
                <label htmlFor="modal-display-name">Display Name</label>
                <input
                  id="modal-display-name"
                  type="text"
                  className="modal-input"
                  value={profile.displayName}
                  onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
                  placeholder="Your Name"
                />
              </div>

              <div className="input-group">
                <label htmlFor="modal-bio">Bio / Motto (Public)</label>
                <textarea
                  id="modal-bio"
                  className="modal-textarea"
                  rows={3}
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  placeholder="A short motto or welcome message for your 3D room visitors..."
                  maxLength={280}
                />
              </div>

              <div className="checkbox-field-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={profile.isPublic}
                    onChange={(e) => setProfile({ ...profile, isPublic: e.target.checked })}
                  />
                  <span>Allow visitors to view my public 3D character profile</span>
                </label>
              </div>
            </div>
          )}

          <div className="customizer-modal-actions">
            <button type="button" className="action-btn cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="action-btn save-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Character'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CharacterCustomizerModal;
